import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import apartmentService from '../services/apartmentService';
import { useUnion } from './UnionContext';


const ApartmentContext = createContext();

export const useApartment = () => {
  const context = useContext(ApartmentContext);
  if (!context) {
    throw new Error('useApartment must be used within ApartmentProvider');
  }
  return context;
};

const storageKey = (unionId) => `apt_active_${unionId}`;

export const ApartmentProvider = ({ children }) => {
  const { activeUnion, isUnionAdmin } = useUnion();
  const [apartments, setApartments] = useState([]);
  const [activeApartment, setActiveApartment] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadApartments = useCallback(async () => {
    if (!activeUnion) {
      setApartments([]);
      setActiveApartment(null);
      return;
    }
    setLoading(true);
    try {
      const data = await apartmentService.getApartments({ mine: 'true' });
      const list = Array.isArray(data) ? data : data.results || [];
      setApartments(list);

      // Restore persisted selection for this union
      const savedId = localStorage.getItem(storageKey(activeUnion.id));
      const saved = savedId ? list.find(a => a.id === parseInt(savedId, 10)) : null;

      // Admins default to no apartment (admin view); residents default to first apartment
      setActiveApartment(saved || (isUnionAdmin ? null : list[0]) || null);
    } catch {
      setApartments([]);
      setActiveApartment(null);
    } finally {
      setLoading(false);
    }
  }, [activeUnion, isUnionAdmin]);

  useEffect(() => {
    loadApartments();
  }, [loadApartments]);

  const switchApartment = useCallback((apartmentId) => {
    const apt = apartments.find(a => a.id === apartmentId) || null;
    setActiveApartment(apt);
    if (activeUnion) {
      if (apt) {
        localStorage.setItem(storageKey(activeUnion.id), apt.id);
      } else {
        localStorage.removeItem(storageKey(activeUnion.id));
      }
    }
  }, [apartments, activeUnion]);

  const value = {
    apartments,
    activeApartment,
    loading,
    switchApartment,
    refreshApartments: loadApartments,
  };

  return (
    <ApartmentContext.Provider value={value}>
      {children}
    </ApartmentContext.Provider>
  );
};
